import React from 'react';

import { Header, ScrollView, makeUclComponent } from '@rbilabs/universal-components';
import { useWindowDimensions } from 'react-native';

import ActionButton from 'components/action-button';
import Picture from 'components/picture';
import SanityBlockRenderer from 'components/sanity-block-renderer';
import { Box, BoxProps } from 'components/ui/box';
import LoyaltyRewardsLogo from 'pages/loyalty/loyalty-rewards-logo';
import { getDesignToken, spacing } from 'styles/constants';
import { breakpoints } from 'styles/constants/breakpoints';

// `marginBottom` is measured (safe-area-derived) and set via the native `style` prop by the
// caller, not through this styled-system config — UCL's Box/ScrollView resolve numeric
// margin/padding props against the theme's space scale (e.g. 80 could resolve to the scale's
// `'80'` key instead of literal pixels), which silently corrupts runtime-computed values.
export const Container = ScrollView.withConfig({
  width: 'full',
});

export const HeadingText = Header.withConfig({
  accessibilityLevel: 1,
  margin: 0,
  color: getDesignToken('color-secondary'),
  fontSize: '5xl',
  lineHeight: '2xs',
  textAlign: 'center',
  textTransform: getDesignToken('text-transform-headlines'),
});

export const ImageContainer = Picture.withConfig<{ $windowHeight?: number }>(
  ({ $windowHeight }) => ({
    width: 'full',
    maxWidth: '80%',
    height: $windowHeight ? { base: 0.2 * $windowHeight, sm: 0.28 * $windowHeight } : 'auto',
    marginY: '$1',
    marginX: 'auto',
  })
);

export const TitleText = Header.withConfig<{ $fontColor?: string }>({
  variant: 'headerTwo',
  accessibilityLevel: 2,
  textTransform: getDesignToken('text-transform-headlines'),
  margin: 0,
  textAlign: 'center',
});

export const SubtitleText = Header.withConfig({
  variant: 'headerFour',
  accessibilityLevel: 4,
  width: '95%',
  margin: 0,
  marginTop: '$4',
  marginBottom: '$8',
  fontSize: 'md',
  fontFamily: 'body',
  textAlign: 'center',
});

export const DescriptionTextBlock = SanityBlockRenderer.withConfig({
  // Horizontal inset on the Box wrapper; Typography ignores NativeBase marginX/marginY.
  paddingX: '$4',
  textProps: {
    my: '$4',
    fontSize: 'md',
    fontFamily: 'body',
  },
  linkProps: {
    top: 0.5,
    underline: true,
  },
});

// Positions and reserves clearance above the floating tab bar. Kept free of border/shadow/
// background so that decoration (see ButtonCard) stays tight around the button instead of
// stretching across the reserved clearance below it.
// `paddingBottom` is measured (safe-area-derived) and set via the native `style` prop by the
// caller instead of here — see the Container comment above for why.
export const ButtonWrapper = ({ style, ...props }: BoxProps) =>
  React.createElement(Box, {
    ...props,
    style: [styles.buttonWrapper, style],
  });

export const ButtonCard = ({
  bottomInset = 0,
  style,
  ...props
}: BoxProps & { bottomInset?: number }) => {
  const { width } = useWindowDimensions();
  const isBaseBreakpoint = width < breakpoints.sm;

  return React.createElement(Box, {
    ...props,
    style: [
      styles.buttonCard,
      isBaseBreakpoint && styles.buttonCardBase,
      !isBaseBreakpoint && styles.buttonCardSm,
      isBaseBreakpoint && !bottomInset && styles.buttonCardBaseBottomBorder,
      style,
    ],
  });
};

export const StyledLoyaltyLogo = LoyaltyRewardsLogo.withConfig({
  alignItems: 'center',
  marginTop: {
    base: '$8',
    lg: '$16',
  },
});

export const StyledActionButton = makeUclComponent(ActionButton).withConfig({
  paddingY: '$3',
  paddingX: '$12',
  _text: {
    fontSize: 'lg',
  },
  _web: {
    maxWidth: '480px',
  },
});

export const Terms = SanityBlockRenderer.withConfig({
  _web: {
    paddingY: '20px',
  },
  textProps: {
    textAlign: 'center',
    marginY: '$4',
    marginX: 'auto',
    fontSize: 'sm',
  },
} as any);

const styles = {
  buttonWrapper: {
    width: '100%' as const,
    position: 'absolute' as const,
    bottom: 0,
  },
  buttonCard: {
    marginTop: spacing.$2,
    alignItems: 'center' as const,
    alignSelf: 'center' as const,
    width: '100%' as const,
    height: spacing.$20,
    paddingHorizontal: spacing.$4,
    justifyContent: 'center' as const,
    borderTopColor: getDesignToken('border-color-default'),
    borderBottomColor: getDesignToken('border-color-default'),
  },
  buttonCardBase: {
    backgroundColor: getDesignToken('background-default'),
    borderTopWidth: 2,
  },
  buttonCardSm: {
    backgroundColor: 'transparent',
  },
  buttonCardBaseBottomBorder: {
    borderBottomWidth: 2,
  },
};
